import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  CircularProgress,
  Chip,
  Alert,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { getUserTransactions } from '../../store/slices/usersSlice';
import { Transaction } from '../../types';
import dayjs from 'dayjs';

interface UserTransactionsModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  username: string;
}

const UserTransactionsModal: React.FC<UserTransactionsModalProps> = ({
  open,
  onClose,
  userId,
  username,
}) => {
  const dispatch = useDispatch();
  const { userTransactions, transactionPagination, loading } = useSelector((state: RootState) => state.users);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (open && userId) {
      dispatch(getUserTransactions({ userId, page, limit: 10 }) as any);
    }
  }, [open, userId, page, dispatch]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'deposit': return 'success';
      case 'withdraw': return 'warning';
      case 'win': return 'info';
      case 'purchase': return 'primary';
      case 'house_fee': return 'error';
      case 'refund': return 'default';
      case 'transfer': return 'secondary';
      default: return 'default';
    }
  };



  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency: 'ETB',
    }).format(amount);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle>
        <Typography variant="h6">Transactions for {username}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : userTransactions.length === 0 ? (
          <Alert severity="info">No transactions found for this user.</Alert>
        ) : (
          <Box>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Balance After</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userTransactions.map((transaction: Transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                        <Chip
                          label={transaction.type}
                          color={getTypeColor(transaction.type) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          color={transaction.amount >= 0 ? 'success.main' : 'error.main'}
                          fontWeight="bold"
                        >
                          {transaction.amount >= 0 ? '+' : ''}{formatCurrency(transaction.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="bold">
                          {formatCurrency(transaction.balanceAfter)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {transaction.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {dayjs(transaction.createdAt).format('MMM DD, YYYY HH:mm')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            {transactionPagination && transactionPagination.pages > 1 && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={transactionPagination.pages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserTransactionsModal; 